/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   CollectionJobsPage.tsx
 *     Feature         :   139-collection-job-console
 *     Description     :   Main page — status, start button, history, detail accordion
 *     Constitution    :   v1.12.0 | Principle XXXVII (Code Headers)
 *     Author          :   Peter Dobler
 *     Created         :   2026-03-02
 *     Modified        :   2026-06-14 | Conformed to Spec 197 design blueprint —
 *                         migrated the page shell onto the canonical UI kit
 *                         (PageLayout / Card / ErrorState); presentation-only,
 *                         no data/logic/state/behavior changes.
 * =========================================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Database } from 'lucide-react';
import { useAuth } from '../../modules/backup/hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import {
  useCollectionJobStatus,
  COLLECTION_JOB_KEYS,
} from '../../services/operations/collectionJobService';
import { FCJLOGS_KEYS } from '../../services/operations/fcjlogsService';
import { PageLayout, Card, ErrorState } from '../../components/ui';
import { CollectionJobStatus } from '../../components/operations/CollectionJobStatus';
import { StartJobButton } from '../../components/operations/StartJobButton';
import { CollectionRunHistory } from '../../components/operations/CollectionRunHistory';


const CollectionJobsPage: React.FC = () => {
  const { isOperatorOrAbove } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: status, isLoading: statusLoading, error: statusError, refetch: refetchStatus } = useCollectionJobStatus();

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const handleSelectRun = (id: string) => {
    setSelectedRunId((prev) => (prev === id ? null : id));
  };

  // Track previous state for completion detection
  const prevStateRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const currentState = status?.state;
    const previousState = prevStateRef.current;
    prevStateRef.current = currentState;

    // Detect running → idle transition (job completed)
    if (previousState === 'running' && currentState === 'idle') {
      const lastStatus = status?.last_run?.status;
      if (lastStatus === 'Completed') {
        toast.success('Collection job completed successfully');
      } else if (lastStatus === 'Partial') {
        toast.warning('Collection job completed with partial results');
      } else if (lastStatus === 'Failed') {
        toast.error('Collection job failed');
      } else {
        toast.info('Collection job finished');
      }

      // Invalidate runs query to refresh history table
      queryClient.invalidateQueries({ queryKey: COLLECTION_JOB_KEYS.all });
    }
  }, [status?.state, status?.last_run?.status, toast, queryClient]);

  const isRunning = status?.state === 'running';

  return (
    <PageLayout
      title="Collection Jobs"
      description="Manage and monitor data collection jobs"
      icon={<Database className="h-6 w-6" />}
      helpId="internal.collection-jobs"
      onRefresh={async () => {
        // Spec 285 / 226: status, run history, run detail AND the step logs of an expanded run.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: COLLECTION_JOB_KEYS.all }),
          queryClient.invalidateQueries({ queryKey: FCJLOGS_KEYS.all }),
        ]);
      }}
      actions={<StartJobButton isRunning={isRunning} canStart={isOperatorOrAbove()} />}
    >
      {statusError ? (
        <ErrorState
          title="Failed to load collection job status"
          onRetry={() => refetchStatus()}
        />
      ) : (
        <div className="space-y-6">
          {/* Status Panel */}
          <CollectionJobStatus status={status} isLoading={statusLoading} />


          {/* Run History + Detail */}
          <Card>
            <CollectionRunHistory
              selectedRunId={selectedRunId}
              onSelectRun={handleSelectRun}
            />
          </Card>
        </div>
      )}
    </PageLayout>
  );
};

export default CollectionJobsPage;
